import { useState, useContext, useEffect, useCallback } from 'react';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FolderIcon from '@mui/icons-material/Folder';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Modal from '@mui/material/Modal';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import NestingMenu from './menu.js';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import WidgetsIcon from '@mui/icons-material/Widgets';
import { FetchContext, NestingContext } from './context.js';
import { style } from './style.js';
import { useNavigate, useParams} from "react-router-dom";

export async function createNesting(_nwsFetch, extraData={}){
	const defaultData = {
		tray: [0, 0, 0, 320, 320, 610],
    ...extraData,
	}
	// a new nesting
	const request = await _nwsFetch(
		"/api/groups/", {
			method: "POST",
			body: JSON.stringify(defaultData),
		},
	);
	return [request.ok, await request.json()];
}

export function CreateNestingButton({onClick, menu=true, folder}){
  const nwsFetch = useContext(FetchContext);
	const navigate = useNavigate();
  const extraData = {};
  if(folder && folder.slug){
    extraData.group = folder.slug;
  }
	async function newGroup(){
		const [ok, nesting] = await createNesting(nwsFetch, extraData);
		if(ok){
      onClick();
			navigate("/nesting/" + nesting.slug);
		}
	}
  if(menu){
    return (
      <Typography color='secondary' onClick={newGroup}>New nesting</Typography>
    )
  }else{
    return (
      <ListItemButton onClick={()=>newGroup()}>
        <ListItemIcon>
          <AddIcon />
        </ListItemIcon>
        <ListItemText primary={"New Nesting"}/>
      </ListItemButton>
    )
  }
}

export function FolderSelectControl({folder, updateUI, onGroup}){
  async function chooseFolder(){
    onGroup(folder);
  }
  return (
    <ListItem>
      <ListItemButton onClick={()=>chooseFolder()}>
        <ListItemIcon>
          <DriveFileMoveIcon/>
        </ListItemIcon>
        <ListItemText primary={"Move"}/>
      </ListItemButton>
    </ListItem>
  )
}

function NestedComponentMove({element, onClose, updateUI}){
  const nwsFetch = useContext(FetchContext);
  async function changeFolder(folder){
    await nwsFetch(
      "/api/containers/" + element.slug + "/", {
        method: "PATCH",
        body: JSON.stringify({
          group: folder.slug 
        })
      }
    );
    updateUI();
  }
  return (
    <GroupList2
      onGroup={changeFolder}
      ElementTag={NestedComponentContainer}
      Controller={FolderSelectControl}
      resultFilter={(result)=>result.type === "cn"}
    />
  )
}

function NestedComponentModalContent({children, onClose, onSubmit, cancel="Cancel", submit="Submit"}){
  return (
    <>
      {children}
      <Container sx={{display: "flex"}}>
          <Button onClick={onClose}>{cancel}</Button>
          <Box sx={{flexGrow: 1}}></Box>
          <Button onClick={()=>{onSubmit();onClose()}}>{submit}</Button>
      </Container>
    </>
  );
}

function NestedComponentDelete({element, onClose, updateUI}){
  const nwsFetch = useContext(FetchContext);
	const name = element.name || element.slug;
  const rootUrl = element.type === "gp"? "/api/groups/" : "/api/containers/";
  async function deleteContainer(){
    await nwsFetch(
      rootUrl + element.slug + "/", {method: "DELETE"}
    );
    updateUI();
  }
  return (
    <NestedComponentModalContent
      onSubmit={deleteContainer}
      onClose={onClose}
      submit="Yes"
      cancel="No"
    >
      <Typography>Are you sure you to delete {name}</Typography> 
    </NestedComponentModalContent>
  );
}

function NestedComponentRename({element, onClose, updateUI}){
  const nwsFetch = useContext(FetchContext);
  const [nesting, setNesting] = useContext(NestingContext);
	const _name = element.name || element.slug;
  const [name, setName] = useState(_name);
  async function renameElement(){
    const response = await nwsFetch(
      "/api/containers/" + element.slug + "/", {
        method: "PATCH",
        body: JSON.stringify({
          name: name
        })
      }
    );
    updateUI();
    if(response.ok && element.slug === nesting.slug){
      setNesting({...nesting, name: name});
    }
  }
  return (
    <NestedComponentModalContent
      onSubmit={renameElement}
      onClose={onClose}
    >
      <TextField
        label="Change name"
        value={name}
        onChange={(evt) => setName( evt.target.value)}
      />
    </NestedComponentModalContent>
  );
}

function NestedComponentCopy({element, onGroup}){
  const nwsFetch = useContext(FetchContext);
	const navigate = useNavigate();
  const disabled = element.type !== "gp";
	async function duplicateGroup() {
		const response = await nwsFetch(
			"/api/groups/" + element.slug + "/copy/", {
				method: "POST",
			}
		);
		if(response.ok){
			const groupCopy = await response.json();
      if(onGroup){
        onGroup(element.slug);
      }
			navigate("/nesting/" + groupCopy.slug);
		}
	}
  return (
    <MenuItem disabled={disabled} onClick={duplicateGroup}>
      <ContentCopyIcon color='secondary'/>
      <Typography color='secondary'>Copy</Typography>
    </MenuItem>
  )
}

function getModalContent(modal, props){
  if(modal === 1){
    return (
      <NestedComponentDelete {...props}/>
    );
  }else if(modal === 2){
    return (
      <NestedComponentRename {...props}/>
    );
  }else if(modal === 3){
    return (
      <NestedComponentMove {...props}/>
    );
  }else{
    return (<></>);
  }
}

function isDeleteDisabled(params, group){
  if(params.hasOwnProperty("nestingSlug") && params.nestingSlug === group.slug){
    return true;
  }else if(group.type === "cn" && group.hasOwnProperty("can_safely_delete_from_components_api")){
    return !group.can_safely_delete_from_components_api;
  }else{
    return false;
  }
}

function NestedComponentMenu({children, element, onGroup, updateUI}){
  const params = useParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState(0);
  const modalContent = getModalContent(
    modal, {element: element, onClose: ()=>setModal(0), updateUI: updateUI}
  );
  const cantMove = element.type === "cn";
  const deleteDisabled = isDeleteDisabled(params, element);
  return (
    <>
      <NestingMenu
        open={menuOpen}
        onOpen={()=>{
          setMenuOpen(true)
        }}
        onClose={()=>{
          setMenuOpen(false)
        }}
        menuName={<MenuIcon/>}
      >
        <NestedComponentCopy element={element} onGroup={onGroup}/>
        <MenuItem
          onClick={() =>{
            setMenuOpen(false);
            setModal(2);
          }}
        >
          <EditNoteIcon color='secondary'/>
          <Typography
            color='secondary'
          >Rename</Typography>
        </MenuItem>
        <MenuItem
          disabled={cantMove}
          onClick={() =>{
            setMenuOpen(false);
            setModal(3);
          }}
        >
          <DriveFileMoveIcon color='secondary'/>
          <Typography color='secondary'>Move</Typography>
        </MenuItem>
        <MenuItem
          disabled={deleteDisabled}
          onClick={() =>{
            setMenuOpen(false);
            setModal(1);
          }}
        >
          <DeleteIcon color='secondary'/> 
          <Typography color='secondary'>Delete</Typography>
        </MenuItem>
      </NestingMenu>
      <Modal open={Boolean(modal)} onClose={()=>setModal(0)}>
        <Box sx={style}>
          {modalContent}
        </Box>
      </Modal>
    </>
  );
}

function Group({group, onGroup}){
	const navigate = useNavigate();
	const groupUrl = "/nesting/" + group.slug;
	const name = group.name || group.slug;
  async function clickGroup(){
    onGroup(group.slug);
    navigate(groupUrl);
  }
	return (
    <>
      <ListItemButton onClick={clickGroup}>
        <ListItemIcon>
          <WidgetsIcon/> 
        </ListItemIcon>
        <ListItemText primary={name} />
      </ListItemButton>
    </>
	)
}

function NestedComponentContainer({element, setFolder}){
  return (
    <>
      <ListItemButton onClick={()=>setFolder(element)}>
        <ListItemIcon>
          <FolderIcon/> 
        </ListItemIcon>
        <ListItemText primary={element.name} />
      </ListItemButton>
    </>
  )
}

function NestedComponent({element, setFolder, updateUI, onGroup}){
  let tag;
  if(element.type === "gp"){
     tag = (
      <Group
        group={element}
        onGroup={onGroup}
      />
    )
  }else{
    tag = (
      <NestedComponentContainer
        element={element}
        setFolder={setFolder}
      />
    )
  }
  return (
    <ListItem>
      {tag}
      <NestedComponentMenu element={element} onGroup={onGroup} updateUI={updateUI}/>
    </ListItem>
  )
}

export function ExplorerControl({folder, updateUI, onGroup}){
  const nwsFetch = useContext(FetchContext);
  async function newFolder(){
    const body = {name: "New folder"};
    if(folder.slug){
      body.group = folder.slug;
    }
    const response = await nwsFetch(
      "/api/containers/", {
        method: "POST",
        body: JSON.stringify(body),
      }
    );
    if(response.ok){
      updateUI();
    }
  }
  return (
    <ListItem>
      <ListItemButton onClick={()=>newFolder()}>
        <ListItemIcon>
          <CreateNewFolderIcon/>
        </ListItemIcon>
        <ListItemText primary={"New folder"}/>
      </ListItemButton>
      <CreateNestingButton
        onClick={()=>{
          updateUI()
          onGroup()
        }}
        menu={false}
        folder={folder}
      />
    </ListItem>
  )
}

const pageLength = 10;

export function GroupListImpl({
  children,
  folder,
  setFolder,
  page,
  setPage,
  onGroup,
  ElementTag,
  Controller,
  resultFilter,
}){
  const nwsFetch = useContext(FetchContext);
  const [response, setResponse] = useState({});
  const [age, setAge] = useState(0);
  useEffect(() => {
    nwsFetch(
      "/api/components/" + folder.slug + "?limit="+ pageLength + "&offset=" + (page)*pageLength
    ).then(async (response) => {
      const data = await response.json();
      setResponse(data);
    });
  }, [nwsFetch, page, setResponse, folder, age]);
  if(response.hasOwnProperty("results")){
    const groups = response.results.filter(resultFilter);
    const pageNumber = Math.ceil(response.count / pageLength); 
    return (
      <Box sx={{display: "flex", flexDirection: "column", height: "100%"}}>
        <Container>
          {children}
        </Container>
        <List dense={true}>
          {groups.map((group) => (
            <ElementTag
              element={group}
              key={group.slug}
              setFolder={setFolder}
              onGroup={onGroup}
              updateUI={() => setAge(age+1)}
            />
          ))}
        </List>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
            <Pagination
              page={page + 1}
              count={pageNumber}
              renderItem={(item) => {
                item.onClick = () => setPage(item.page - 1);
                return (<PaginationItem
                  {...item}
                />);
              }}
            />
        </Box>
        <Box
          sx={{marginTop: "auto"}}
        >
          <Controller folder={folder} updateUI={()=>setAge(age+1)} onGroup={onGroup}/>
        </Box>
      </Box>
    )
  }else{
    return null;
  }
}

export function GroupList2({
  onGroup,
  ElementTag=NestedComponent,
  Controller=ExplorerControl,
  resultFilter=(result)=>true,
}){
  const [page, setPage] = useState(0);
  const [breadCrumbs, setBreadCrumbs] = useState([]);
  const setFolder = useCallback((folder) => {
    setBreadCrumbs([...breadCrumbs, folder]);
  }, [breadCrumbs, setBreadCrumbs]);
  const folder = breadCrumbs.length > 0 ? breadCrumbs[breadCrumbs.length - 1] : {name: "root", slug: ""};
  const displayedCrumbs = [{name: "root", slug: ""}].concat(breadCrumbs);
  return (
    <GroupListImpl
      folder={folder}
      setFolder={setFolder}
      page={page}
      setPage={setPage}
      onGroup={onGroup}
      ElementTag={ElementTag}
      Controller={Controller}
      resultFilter={resultFilter}
    >
      <Breadcrumbs>
        {displayedCrumbs.map((folder, i) => (
          <Link
            underline="hover"
            color="inherit"
            key={folder.slug ? folder.slug : "root"}
            onClick={()=>setBreadCrumbs(displayedCrumbs.slice(1, i+1))}
            sx={{minWidth: 0, padding:"1px", cursor: "pointer"}}
          >
            {folder.name}
          </Link>
        ))}
      </Breadcrumbs>
    </GroupListImpl>
  )
}

export function GroupList(){
	const params = useParams();
  const page = parseFloat(params.offset) + 1;
	const navigate = useNavigate();
  const setPage = (n) => navigate("/groups/" + n);
	return (
    <GroupListImpl page={page} setPage={setPage}/>
	)
}

export async function groupLoader({params}){
	const response = await fetch("/api/groups/?limit="+ pageLength + "&offset=" + params.offset*pageLength);
	return await response.json();
}
